import { distanceBetween } from './geo'

export interface GeoLocation {
  lat: number
  lng: number
}

export interface BookingLocationValue extends GeoLocation {
  address: string
}

interface ResolveBookingLocationsInput {
  vehicleHomeAddress: string | null | undefined
  vehicleHomeLocation: GeoLocation | null | undefined
  pickupAddress: string | null | undefined
  pickupLat: number | null | undefined
  pickupLng: number | null | undefined
  dropoffAddress: string | null | undefined
  dropoffLat: number | null | undefined
  dropoffLng: number | null | undefined
}

export interface ResolvedBookingLocations {
  hasVehicleHomeLocation: boolean
  hasDraftPickupLocation: boolean
  hasDraftDropoffLocation: boolean
  pickupLocation: BookingLocationValue | null
  dropoffLocation: BookingLocationValue | null
}

function isFiniteCoordinate(lat: number | null | undefined, lng: number | null | undefined) {
  return Number.isFinite(lat) && Number.isFinite(lng)
}

function cleanAddress(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function completeDraftLocation(
  address: string | null | undefined,
  lat: number | null | undefined,
  lng: number | null | undefined,
): BookingLocationValue | null {
  const clean = cleanAddress(address)
  if (!clean || !isFiniteCoordinate(lat, lng)) return null
  return { address: clean, lat: lat!, lng: lng! }
}

export function resolveBookingLocations(
  input: ResolveBookingLocationsInput,
): ResolvedBookingLocations {
  const homeAddress = cleanAddress(input.vehicleHomeAddress)
  const homeLocation = input.vehicleHomeLocation

  if (!homeAddress || !homeLocation || !isFiniteCoordinate(homeLocation.lat, homeLocation.lng)) {
    return {
      hasVehicleHomeLocation: false,
      hasDraftPickupLocation: false,
      hasDraftDropoffLocation: false,
      pickupLocation: null,
      dropoffLocation: null,
    }
  }

  const home: BookingLocationValue = {
    address: homeAddress,
    lat: homeLocation.lat,
    lng: homeLocation.lng,
  }
  const draftPickup = completeDraftLocation(input.pickupAddress, input.pickupLat, input.pickupLng)
  const pickupLocation = draftPickup ?? home
  const draftDropoff = completeDraftLocation(
    input.dropoffAddress,
    input.dropoffLat,
    input.dropoffLng,
  )

  return {
    hasVehicleHomeLocation: true,
    hasDraftPickupLocation: draftPickup !== null,
    hasDraftDropoffLocation: draftDropoff !== null,
    pickupLocation,
    dropoffLocation: draftDropoff ?? pickupLocation,
  }
}

export function haversineKm(a: GeoLocation, b: GeoLocation): number {
  return distanceBetween(a.lat, a.lng, b.lat, b.lng) / 1000
}

export function isWithinDeliveryRadius(params: {
  hostLocation: GeoLocation
  location: GeoLocation
  deliveryRadiusKm: number
}): boolean {
  return haversineKm(params.hostLocation, params.location) <= params.deliveryRadiusKm
}

export function areBookingLocationsWithinRadius(params: {
  hostLocation: GeoLocation | null | undefined
  pickupLocation: GeoLocation | null | undefined
  dropoffLocation: GeoLocation | null | undefined
  deliveryRadiusKm: number
}): boolean {
  if (!params.hostLocation || !params.pickupLocation || !params.dropoffLocation) return false
  return (
    isWithinDeliveryRadius({
      hostLocation: params.hostLocation,
      location: params.pickupLocation,
      deliveryRadiusKm: params.deliveryRadiusKm,
    }) &&
    isWithinDeliveryRadius({
      hostLocation: params.hostLocation,
      location: params.dropoffLocation,
      deliveryRadiusKm: params.deliveryRadiusKm,
    })
  )
}
